import { Link } from "wouter";
import { isNativeApp } from "../../lib/platform";
import { SlidersHorizontal, LayoutGrid, List, Loader2, SearchX, X, Plus, Compass } from "lucide-react";

import { Tabs, TabsList, TabsTrigger } from "../../components/beui/Tabs";
import { SharedLayoutBg } from "../../components/motion/shared-layout-bg";

import { SmartSelect } from "../../components/SmartSelect";
import { JobseekerCardSkeleton, ServiceCardSkeleton, CompanyCardSkeleton, MarketplaceListCardSkeleton, PublicProfileCard, PublicProfileListCard, ServiceCard, ServiceListCard, CompanyCard, CompanyListCard, JobCard, ProjectCard, RfqCard, ItemCard } from "../../components/cards";

import { SearchInput } from "../../components/SearchInput";
import { DiscoverySort } from "../../components/DiscoverySort";

import { hasRating, hasPriceSort, DIRECTORY_GROUPS, TAB_PRESENTATION, SURFACE_META } from "./model";
import type { MarketplaceState } from "./useMarketplaceController";
import { createMarketplaceControls } from "./MarketplaceControls";
export function MarketplaceView(state: MarketplaceState) {
  // Native discovery inputs stay at 16px to prevent WKWebView focus zoom.
  const nativeInputClass = isNativeApp() ? "[&_input]:text-base" : "";
const { lang, t, tabLabel, groupLabel, locked, meta, tab, f, view, loading, loadingMore, loadMoreSentinelRef, fallback, loadError, setRetryKey, layout, setLayout, cityOptions, showFilters, setShowFilters, isWorker, applicationStatuses, applicationStatusesState, set, resetFilters, orgTypes, items, count, activeGroup, intentKeys, presentationIntent, visibleTotal, sectionSelections, filtersActive, activeCount, selectTab, loadMore, refreshing }=state;
const { filterGroups, updateSelections, sectionFilterControls, qualityFilterControls, desktopViewControls } = createMarketplaceControls(state);
return (
    <div className={`container-x py-6 ${nativeInputClass}`}>
      {meta ? (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-navy text-white grid place-items-center shrink-0"><meta.Icon className="w-5 h-5" /></div>
            <div>
              <h1 className="text-xl font-extrabold text-navy leading-tight">{tabLabel(meta.title)}</h1>
              <p className="text-sm text-mut">{t(intentKeys[locked || ""] || "discovery.servicesIntent", meta.subtitle)}</p>
            </div>
          </div>
          <Link href={meta.cta.href} className="btn-red h-10 px-4 text-sm"><Plus className="w-4 h-4" />{t(({ Jobseekers: "discovery.jobseekersCta", Services: "discovery.offerServiceCta", Companies: "discovery.addCompanyCta", Jobs: "discovery.postJobCta", "Project Bidding": "discovery.postProjectCta" } as Record<string, string>)[locked || ""] || "discovery.offerServiceCta", meta.cta.label)}</Link>
        </div>
      ) : (
        <div className="mb-5 flex items-start gap-4">
          <Tabs value={activeGroup.key} onValueChange={(key) => {
            const group = DIRECTORY_GROUPS.find(group => group.key === key);
            if (group) selectTab(group.tabs[0]);
          }} variant="underline" className="min-w-0 flex-1">
            <TabsList aria-label="Marketplace sections" className="w-full">
              {DIRECTORY_GROUPS.map(group => <TabsTrigger key={group.key} value={group.key} aria-label={groupLabel(group)} icon={<group.Icon className="hidden h-4 w-4 shrink-0 sm:block" aria-hidden="true" />} indicatorClassName="bg-brand-red" className="min-h-12 flex-1 justify-center px-1 text-xs sm:px-4 sm:text-sm">
                <span className="sm:hidden">{group.key === "providers" ? (lang === "tl" ? "Mga Provider" : "Providers") : group.key === "opportunities" ? (lang === "tl" ? "Oportunidad" : "Opportunities") : t("discovery.marketplace")}</span>
                <span className="hidden sm:inline">{groupLabel(group)}</span>
              </TabsTrigger>)}
            </TabsList>
          </Tabs>
          {desktopViewControls}
        </div>
      )}
      {/* v3.9.49 — quick jump between directories (visible in locked/surface mode). */}
      {meta && (
        <div className="mb-5 flex items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {["Jobseekers", "Services", "Companies", "Jobs", "Project Bidding"].map((t) => (
              <Link key={t} href={SURFACE_META[t].route}
                className={`chip ${t === locked ? "bg-navy text-white" : "bg-navy-50 text-navy-600 hover:bg-navy hover:text-white"}`}>
                {tabLabel(t)}
              </Link>
            ))}
            <Link href="/marketplace" className="chip bg-navy-50 text-navy-600 hover:bg-navy hover:text-white">{t("discovery.all")} ▸</Link>
          </div>
          {desktopViewControls}
        </div>
      )}
      <div className="grid lg:grid-cols-[300px_minmax(0,1fr)] gap-6">
        {/* FILTERS */}
        <aside aria-label={t("discovery.refine")} className="card sticky top-24 self-start p-5 h-fit hidden lg:block desktop-sidebar-scroll">
          {!meta && activeGroup.tabs.length > 1 && (
            <div className="mb-6 border-b border-line pb-6">
              <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-navy"><Compass className="h-4 w-4" />{t("discovery.browse")}</h2>
              <nav aria-label={`${groupLabel(activeGroup)} directories`}>
                <SharedLayoutBg inset={0} className="flex flex-col gap-1" pillClassName="bg-navy-50 rounded-lg">
                {activeGroup.tabs.map((directoryTab) => {
                  const Icon = TAB_PRESENTATION[directoryTab].Icon;
                  return (
                    <button
                      key={directoryTab}
                      type="button"
                      aria-pressed={directoryTab === tab}
                      onClick={() => selectTab(directoryTab)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${directoryTab === tab ? "bg-navy-50 font-semibold text-navy" : "font-medium text-mut hover:bg-navy-50 hover:text-navy"}`}
                    >
                      <span className="flex items-center gap-3"><Icon aria-hidden="true" className="h-5 w-5 shrink-0" />{tabLabel(directoryTab)}</span>
                    </button>
                  );
                })}
                </SharedLayoutBg>
              </nav>
            </div>
          )}
          <h2 className={`mb-5 flex items-center gap-2 text-navy ${tab === "Services" ? "text-base font-semibold" : "text-lg font-bold"}`}><SlidersHorizontal className="w-4 h-4" />{t("discovery.refine")}</h2>
          <div className="space-y-6">
            <div><label className="mb-2 block text-[13px] font-medium text-mut" htmlFor="marketplace-desktop-search">{t("discovery.keywordsLabel")}</label>
              <SearchInput id="marketplace-desktop-search" value={f.q} onChange={(value) => set({ q: value })} placeholder={t("discovery.keywords")} className="field pl-9" />
            </div>
            <div><label className="mb-2 block text-[13px] font-medium text-mut" htmlFor="marketplace-location-desktop">{t("discovery.locationLabel")}</label>
              <SmartSelect id="marketplace-location-desktop" value={f.city} onChange={(city) => set({ city })} options={cityOptions} placeholder={t("home.search.city")} preserveValueOnFocus />
            </div>
            {qualityFilterControls}
            {sectionFilterControls("desktop")}
            {tab === "Jobseekers" && <div><label className="label" htmlFor="jobseeker-profession-filter">{t("discovery.verifiedProfession")}</label>
              <input id="jobseeker-profession-filter" className="field" value={f.profession} onChange={(e) => set({ profession: e.target.value })} placeholder={t("discovery.professionExample")} maxLength={160} />
            </div>}
            <div className="flex gap-2 pt-2">
              <button onClick={resetFilters} disabled={!filtersActive} className="btn-outline flex-1 h-9 text-xs disabled:cursor-not-allowed disabled:opacity-50">{t("discovery.clear")}</button>
            </div>
          </div>
        </aside>

        {/* RESULTS */}
        <div className="min-w-0">
          {/* Mobile toolbar: one search row followed by directory tabs. */}
          <div className="lg:hidden mb-4 space-y-3">
            <div className="flex min-w-0 items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <SearchInput
                  id="marketplace-mobile-search"
                  value={f.q}
                  onChange={(value) => set({ q: value })}
                  placeholder={lang === "tl" ? t("discovery.keywords") : "Search…"}
                  wrapperClassName="w-full"
                  className="field w-full pl-3 pr-10"
                  iconClassName={f.q ? "hidden" : "absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mut"}
                />
                {f.q && <button type="button" onClick={() => set({ q: "" })} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-mut hover:text-navy"><X className="h-4 w-4" /></button>}
              </div>
              <button type="button" onClick={() => setShowFilters(true)} aria-label={t("discovery.filters")} title={t("discovery.filters")} className="btn-outline relative h-10 w-10 shrink-0 p-0">
                <SlidersHorizontal className="h-4 w-4" />
                {activeCount > 0 && <span className="absolute -right-1 -top-1 rounded-full bg-brand-red px-1 text-[10px] text-white">{activeCount}</span>}
              </button>
              <Tabs value={layout} onValueChange={(value) => setLayout(value as "grid" | "list")} variant="segment" className="shrink-0">
                <TabsList aria-label="Result layout" className="border border-line">
                  <TabsTrigger value="grid" aria-label="Grid view" className="h-9 px-2.5"><LayoutGrid className="h-4 w-4" /></TabsTrigger>
                  <TabsTrigger value="list" aria-label="List view" className="h-9 px-2.5"><List className="h-4 w-4" /></TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            {!meta && activeGroup.tabs.length > 1 && (
              <nav aria-label={`${groupLabel(activeGroup)} directories`} className="grid grid-cols-3 gap-2">
                {activeGroup.tabs.map((directoryTab) => (
                  <button
                    key={directoryTab}
                    type="button"
                    aria-pressed={directoryTab === tab}
                    onClick={() => selectTab(directoryTab)}
                    className={`min-w-0 rounded-lg px-1.5 py-2 text-center text-xs font-semibold transition-colors ${lang === "tl" ? "whitespace-normal leading-tight" : "whitespace-nowrap"} ${directoryTab === tab ? "bg-navy text-white" : "bg-navy-50 text-navy hover:bg-navy hover:text-white"}`}
                  >
                    {tabLabel(directoryTab)}
                  </button>
                ))}
              </nav>
            )}
          </div>

          {!meta && (
            <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
              <p className={`font-bold text-navy ${tab === "Services" ? "text-base sm:text-lg" : "text-sm sm:text-base"}`}>{presentationIntent}</p>
              <span className="shrink-0 text-xs text-mut" aria-live="polite">{visibleTotal.toLocaleString()} {t("discovery.results")}</span>
            </div>
          )}

          {loading && ["Jobseekers", "Services", "Companies", "Jobs", "Project Bidding", "Marketplace"].includes(tab) ? (
            <div role="status" aria-label={`Loading ${tab.toLowerCase()}`} className={layout === "list" ? "grid grid-cols-1 items-stretch gap-4" : "grid grid-cols-2 items-stretch gap-3 sm:gap-4 xl:grid-cols-3"}>
              {(layout === "list" ? [0] : [0, 1, 2]).map((index) => layout === "list" && ["Jobs", "Project Bidding", "Marketplace"].includes(tab)
                ? <MarketplaceListCardSkeleton key={index} />
                : tab === "Jobseekers"
                  ? <JobseekerCardSkeleton key={index} list={layout === "list"} />
                  : tab === "Services"
                    ? <ServiceCardSkeleton key={index} list={layout === "list"} />
                    : tab === "Companies"
                      ? <CompanyCardSkeleton key={index} list={layout === "list"} />
                      : <div key={index} className="card overflow-hidden motion-safe:animate-pulse" aria-hidden="true"><div className="aspect-[4/3] bg-slate-200" /><div className="space-y-3 p-4"><div className="h-4 w-4/5 rounded-full bg-slate-200" /><div className="h-3 w-2/3 rounded-full bg-slate-100" /><div className="h-9 w-28 rounded-lg bg-slate-200" /></div></div>)}
            </div>
          ) : count === 0 && !loading ? (
            <div className="card p-10 text-center">
              <SearchX className="w-8 h-8 text-mut mx-auto mb-2" />
              {fallback || loadError ? (
                <>
                  <div className="font-bold text-navy">{fallback ? t("discovery.temporarilyUnavailable") : t("discovery.loadFailed")}</div>
                  <p className="text-sm text-mut mt-1">{t("discovery.loadFailedHint")}</p>
                  <button onClick={() => setRetryKey((key) => key + 1)} className="btn-outline mt-4 inline-flex">{t("discovery.tryAgain")}</button>
                </>
              ) : filtersActive ? (
                <>
                  <div className="font-bold text-navy">{lang === "tl" ? t("discovery.noResults") : `No ${tab.toLowerCase()} match your filters`}</div>
                  <p className="text-sm text-mut mt-1">{t("discovery.broadenSearch")}</p>
                  <button onClick={resetFilters} className="btn-outline mt-4 inline-flex">{t("discovery.clearFilters")}</button>
                </>
              ) : (
                <>
                  <div className="font-bold text-navy">{lang === "tl" ? t("discovery.noResults") : `No ${tab.toLowerCase()} listed here yet`}</div>
                  <p className="text-sm text-mut mt-1">
                    {tab === "Jobseekers" ? t("discovery.jobseekersEmptyHint") : t("discovery.emptyHint")}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center mt-4">
                    {["Services", "Companies", "Jobs", "Project Bidding"].filter((t) => t !== tab).map((t) => (
                      locked
                        ? <Link key={t} href={SURFACE_META[t].route} className="btn-outline h-9 px-3 text-sm">{tabLabel(t)}</Link>
                        : <button key={t} onClick={() => selectTab(t)} className="btn-outline h-9 px-3 text-sm">{tabLabel(t)}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className={layout === "list" ? "grid grid-cols-1 items-stretch gap-4" : "grid grid-cols-2 items-stretch gap-3 sm:gap-4 xl:grid-cols-3"}>
              {view.tab === "Jobseekers" && items.map((p) => layout === "list" ? <PublicProfileListCard key={p.id} profile={p} /> : <PublicProfileCard key={p.id} profile={p} />)}
              {view.tab === "Jobseekers" && loadingMore && <JobseekerCardSkeleton list={layout === "list"} />}
              {view.tab === "Services" && items.map((s) => layout === "list" ? <ServiceListCard key={s.id} s={s} /> : <ServiceCard key={s.id} s={s} />)}
              {view.tab === "Services" && loadingMore && <ServiceCardSkeleton list={layout === "list"} />}
              {view.tab === "Companies" && items.map((c) => layout === "list" ? <CompanyListCard key={c.id} c={c} /> : <CompanyCard key={c.id} c={c} webGrid />)}
              {view.tab === "Companies" && loadingMore && <CompanyCardSkeleton list={layout === "list"} />}
              {view.tab === "Jobs" && items.map((j) => <JobCard key={j.id} j={j} list={layout === "list"} applicationStatus={isWorker && applicationStatusesState === "loading" ? "loading" : isWorker && applicationStatusesState === "error" ? "unavailable" : applicationStatuses[j.id] as "applied" | "shortlisted" | "accepted" | "rejected" | "withdrawn" | undefined} />)}
              {view.tab === "Project Bidding" && items.map((p) => <ProjectCard key={p.id} p={p} list={layout === "list"} />)}
              {view.tab === "RFQs" && items.map((r) => <RfqCard key={r.id} r={r} list={layout === "list"} />)}
              {view.tab === "Marketplace" && items.map((it) => <ItemCard key={it.id} it={it} list={layout === "list"} />)}
            </div>
          )}
          {view.nextCursor && !fallback && (
            <div ref={loadMoreSentinelRef} className="flex justify-center mt-6">
              <button type="button" onClick={loadMore} disabled={loadingMore || refreshing} className="btn-outline h-10 px-5 min-w-36">
                {loadingMore ? <><Loader2 className="w-4 h-4 animate-spin" /><span className="animate-pulse">{t("discovery.loadingMore")}</span></> : t("discovery.loadMore")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter bottom sheet */}
      {showFilters && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowFilters(false)} aria-hidden="true" />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-line">
              <span className="font-bold text-navy flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> {t("discovery.filters")}</span>
              <button onClick={() => setShowFilters(false)} aria-label="Close" className="btn-ghost h-9 w-9 p-0"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-6 overflow-auto">
              <div><label className="label" htmlFor="marketplace-sort-mobile">{t("discovery.sortBy")}</label>
                <DiscoverySort key={tab} id="marketplace-sort-mobile" className="h-11 w-full text-sm" value={f.sort} onChange={(sort) => set({ sort })} showRating={hasRating(tab)} showPrice={hasPriceSort(tab)} />
              </div>
              <div><label className="mb-2 block text-[13px] font-medium text-mut" htmlFor="marketplace-location-mobile">{t("discovery.locationLabel")}</label>
                <SmartSelect id="marketplace-location-mobile" value={f.city} onChange={(city) => set({ city })} options={cityOptions} placeholder={t("home.search.city")} preserveValueOnFocus />
              </div>
            {sectionFilterControls("mobile")}
            {tab === "Jobseekers" && <div><label className="label" htmlFor="jobseeker-profession-filter-mobile">{t("discovery.verifiedProfession")}</label>
                <input id="jobseeker-profession-filter-mobile" className="field" value={f.profession} onChange={(e) => set({ profession: e.target.value })} placeholder={t("discovery.professionExample")} maxLength={160} />
              </div>}
              {qualityFilterControls}
            </div>
            <div className="flex gap-2 p-4 border-t border-line pb-[calc(env(safe-area-inset-bottom)+16px)]">
              <button onClick={resetFilters} disabled={!filtersActive} className="btn-outline flex-1 disabled:cursor-not-allowed disabled:opacity-50">{t("discovery.clear")}</button>
              <button onClick={() => setShowFilters(false)} className="btn-red flex-1">{lang === "tl" ? `Ipakita ang ${visibleTotal.toLocaleString()} ${t("discovery.results")}` : `Show ${visibleTotal.toLocaleString()} results`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}
